// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React from 'react';

import {UserProfile} from 'mattermost-redux/types/users';
import {Channel, ChannelStats, ChannelMembership} from 'mattermost-redux/types/channels';

import Constants from 'utils/constants';
import * as UserAgent from 'utils/user_agent';

import ChannelMembersDropdown from 'components/channel_members_dropdown';
import SearchableUserList from 'components/searchable_user_list/searchable_user_list_container';
import LoadingScreen from 'components/loading_screen';

import {SearchTimeMeasurer, sendToStatist, Constants as StatistConstants} from '@time-webkit/statist';
import EventEmitter from 'mattermost-redux/utils/event_emitter';

const USERS_PER_PAGE = 50;

export type Props = {
    currentTeamId: string;
    currentChannelId: string;
    searchTerm: string;
    usersToDisplay: UserProfile[];
    actionUserProps: {
        [userId: string]: {
            channel?: Channel;
            teamMember: any;
            channelMember?: ChannelMembership;
        };
    };
    totalChannelMembers: number;
    channel: Channel;
    actions: {
        searchProfiles: (term: string, options?: Record<string, unknown>) => Promise<{data: UserProfile[]}>;
        getChannelMembers: (channelId: string) => Promise<{data: ChannelMembership[]}>;
        getChannelStats: (channelId: string) => Promise<{data: ChannelStats}>;
        setModalSearchTerm: (term: string) => Promise<{data: boolean}>;
        loadProfilesAndTeamMembersAndChannelMembers: (
            page: number,
            perPage: number,
            teamId?: string,
            channelId?: string,
            options?: any
        ) => Promise<{
            data: boolean;
        }>;
        loadStatusesForProfilesList: (users: UserProfile[]) => Promise<{data: boolean}>;
        loadTeamMembersAndChannelMembersForProfilesList: (
            profiles: any,
            teamId: string,
            channelId: string
        ) => Promise<{
            data: boolean;
        }>;
    };
}

type State = {
    loading: boolean;
}

export default class MemberListChannel extends React.PureComponent<Props, State> {
    private searchTimeoutId: number;
    private searchTimeMeasurer: SearchTimeMeasurer;

    constructor(props: Props) {
        super(props);

        this.searchTimeoutId = 0;

        this.state = {
            loading: true,
        };

        this.searchTimeMeasurer = new SearchTimeMeasurer('channel.membersView.result');
    }

    async componentDidMount() {
        const {
            actions,
            currentChannelId,
            currentTeamId,
        } = this.props;

        this.searchTimeMeasurer.measure('');
        await Promise.all([
            actions.loadProfilesAndTeamMembersAndChannelMembers(0, Constants.PROFILE_CHUNK_SIZE, currentTeamId, currentChannelId, {active: true}),
            actions.getChannelMembers(currentChannelId),
            actions.getChannelStats(currentChannelId),
        ]);
        this.searchTimeMeasurer.measure('', this.props.usersToDisplay.length, {channelid: currentChannelId});
        this.loadComplete();
        this.registerAnalyticEvents();
    }

    registerAnalyticEvents() {
        EventEmitter.addListener(StatistConstants.AnalyticEvents.PROFILE_POPOVER_OPENED, this.handleProfilePopoverOpened);
        EventEmitter.addListener(StatistConstants.AnalyticEvents.CHANNEL_MEMBER_REMOVED, this.handleChannelMemberRemoved);
    }

    unregisterAnalyticEvents() {
        EventEmitter.removeListener(StatistConstants.AnalyticEvents.PROFILE_POPOVER_OPENED, this.handleProfilePopoverOpened);
        EventEmitter.removeListener(StatistConstants.AnalyticEvents.CHANNEL_MEMBER_REMOVED, this.handleChannelMemberRemoved);
    }

    handleProfilePopoverOpened = (userId: UserProfile['id']) => {
        sendToStatist('channel.membersView.resultTap', {
            resultAction: 'profile',
            resultNumber: this.props.usersToDisplay.findIndex((u) => u.id === userId) + 1,
            channelid: this.props.currentChannelId,
        });
    }

    handleChannelMemberRemoved = (index: number) => {
        sendToStatist('channel.membersView.resultTap', {
            resultAction: 'remove',
            resultNumber: index + 1,
            channelid: this.props.currentChannelId,
        });
    }

    componentWillUnmount() {
        this.props.actions.setModalSearchTerm('');
        this.unregisterAnalyticEvents();
        this.searchTimeMeasurer.dispose();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.searchTerm !== this.props.searchTerm) {
            clearTimeout(this.searchTimeoutId);
            const searchTerm = this.props.searchTerm;

            if (searchTerm === '') {
                this.loadComplete();
                this.searchTimeoutId = 0;
                return;
            }

            const searchTimeoutId = window.setTimeout(
                async () => {
                    this.searchTimeMeasurer.measure(searchTerm);

                    const {data} = await prevProps.actions.searchProfiles(searchTerm, {team_id: this.props.currentTeamId, in_channel_id: this.props.currentChannelId});

                    if (searchTimeoutId !== this.searchTimeoutId) {
                        return;
                    }

                    this.props.actions.loadStatusesForProfilesList(data);
                    this.props.actions.loadTeamMembersAndChannelMembersForProfilesList(data, this.props.currentTeamId, this.props.currentChannelId).then(({data: membersLoaded}) => {
                        if (membersLoaded) {
                            this.loadComplete(() => {
                                this.searchTimeMeasurer.measure(
                                    searchTerm,
                                    this.props.usersToDisplay.length,
                                    {channelid: this.props.currentChannelId},
                                );
                            });
                        }
                    });
                },
                Constants.SEARCH_TIMEOUT_MILLISECONDS,
            );

            this.searchTimeoutId = searchTimeoutId;
        }
    }

    loadComplete = (cb?: () => void) => {
        this.setState({loading: false}, cb);
    }

    nextPage = (page: number) => {
        this.props.actions.loadProfilesAndTeamMembersAndChannelMembers(page + 1, USERS_PER_PAGE, undefined, undefined, {active: true});
    }

    handleSearch = (term: string) => {
        this.props.actions.setModalSearchTerm(term);
    }

    render() {
        if (this.state.loading) {
            return (<LoadingScreen/>);
        }
        const channelIsArchived = this.props.channel.delete_at !== 0;
        return (
            <SearchableUserList
                users={this.props.usersToDisplay}
                usersPerPage={USERS_PER_PAGE}
                total={this.props.totalChannelMembers}
                nextPage={this.nextPage}
                search={this.handleSearch}
                actions={channelIsArchived ? [] : [ChannelMembersDropdown]}
                actionUserProps={this.props.actionUserProps}
                focusOnMount={!UserAgent.isMobile()}
            />
        );
    }
}
